import { FCSecurePage, useRegisterRefresh, useShopPersistence } from '@/components/shared/navigation/securePage.ts';
import { NewPageTitleBar } from '@/components';
import { getMenuData } from '@core/data/menuData.ts';
import { Box, Button, Container } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import ShopSelector from '@/components/selectors/ShopSelector.tsx';
import TabSelector, { TabItem } from '@/components/shared/TabSelector.tsx';
import { HardwareComponents, ShopInformation, SoftwareModules } from '@features/administration/components/shopSetup';
import { ShopEditorMode, ShopInformationRef } from '@features/administration/components/shopSetup/ShopInformation.tsx';
import { shopService } from '@/services';
import { Shop } from '@core/types';
import { useRegisterDirtyGuard } from '@core/hooks/useRegisterDirtyGuard.ts';
import { useUnsavedChangesGuard } from '@core/hooks/useUnsavedChangesGuard.ts';
import { useNotifications } from '@core/hooks/useNotifications.ts';

const ShopSetup: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [activeTab, setActiveTab] = useState(0);
    const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const shopInfoRef = useRef<ShopInformationRef>(null);
    const discardedRef = useRef(false);
    const selectShopSectionRef = useRef<HTMLDivElement>(null);
    const { notifySuccess } = useNotifications();
    const [copyReferenceShopId, setCopyReferenceShopId] = useState<string | null>(null);

    const shopId = selectedShopIds[0] ?? null;
    const creating = isCreating || !shopId;

    const load = useCallback(async () => {
        if (!shopId) {
            setSelectedShop(null);
            return;
        }

        setLoading(true);
        try {
            const shop = await shopService.loadShop(shopId);
            setLastUpdate(DateTime.now());
            setSelectedShop(shop);
        } catch (error) {
            console.error('[ShopSetup] Failed to load:', error);
        } finally {
            setLoading(false);
        }
    }, [shopId]);

    useEffect(() => {
        load();
    }, [load]);

    useRegisterRefresh(load, loading);

    const isDirty = useCallback(() => !discardedRef.current && !!shopInfoRef.current?.isDirty, []);

    const save = useCallback(async () => {
        const saved = await shopInfoRef.current?.save();
        return !!saved;
    }, []);

    const discard = useCallback(() => {
        discardedRef.current = true;
        setIsCreating(false);
    }, []);

    const { guard } = useUnsavedChangesGuard({ isDirty, save });
    useRegisterDirtyGuard({ checkDirty: isDirty, save, discard });

    const handleTabChange = useCallback(
        (newValue: number) => {
            guard(() => {
                discardedRef.current = false;
                setActiveTab(newValue);
                setIsCreating(false);
            });
        },
        [guard]
    );

    const [editorNonce, setEditorNonce] = useState(0);

    const handleCancelShopInfo = useCallback(() => {
        guard(() => {
            discardedRef.current = true;
            setCopyReferenceShopId(null);
            setIsCreating(false);
            setEditorNonce((n) => n + 1);
            selectShopSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, [guard]);

    const handleCloseShopInfo = (closedShopId?: string) => {
        discardedRef.current = true;
        setIsCreating(false);
        if (closedShopId && closedShopId !== shopId) {
            setSelectedShopIds([closedShopId]);
        } else {
            load();
            if (!closedShopId) setIsCreating(true);
        }
        selectShopSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const handleCloseComponent = (closedShopId?: string) => {
        discardedRef.current = true;
        setIsCreating(false);
        if (closedShopId) {
            load();
        }
        selectShopSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const handleCloseModule = (closedShopId?: string) => {
        discardedRef.current = true;
        setIsCreating(false);
        if (closedShopId) {
            load();
        }
    };

    const handleUpdateCloudConfig = useCallback(async () => {
        setLoading(true);
        try {
            await shopService.updateShopCloudConfig();
            notifySuccess();
        } catch (error) {
            console.error('Failed to update cloud config:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleCopyShop = useCallback(() => {
        guard(() => {
            discardedRef.current = false;
            setCopyReferenceShopId(shopId);
            setIsCreating(true);
        });
    }, [guard, shopId]);

    const shopForEditor = selectedShop?.id === shopId ? selectedShop : null;

    const tabs: TabItem[] = [
        { key: 'shopInformation', label: t('shopInformation') },
        {
            key: 'hardwareComponents',
            label: t('hardwareComponents'),
            disabled: !shopForEditor || (activeTab === 0 && isCreating)
        },
        {
            key: 'softwareModules',
            label: t('softwareModules'),
            disabled: !shopForEditor?.gateways?.length || (activeTab === 0 && isCreating)
        }
    ];

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.default' }}>
            <NewPageTitleBar
                icon={<i className="bi bi-motherboard"></i>}
                title={t('shopSetup')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-shop/shopsetup' }}
                t={t}
                lastUpdate={lastUpdate}
            />
            <Container maxWidth={false} sx={{ mt: 3, pb: 3 }}>
                <Box
                    ref={selectShopSectionRef}
                    sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}
                >
                    <Box>
                        <ShopSelector
                            value={shopId}
                            onChange={(id) =>
                                guard(() => {
                                    discardedRef.current = false;
                                    setIsCreating(false);
                                    setSelectedShopIds(id ? [id] : []);
                                })
                            }
                        />
                    </Box>
                    <Button
                        variant="text"
                        disabled={loading || creating}
                        className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                        sx={{ height: 40, placeSelf: 'flex-end' }}
                        onClick={() =>
                            guard(() => {
                                discardedRef.current = false;
                                setCopyReferenceShopId(null);
                                setIsCreating(true);
                            })
                        }
                    >
                        <Box className="pagetitlebar-action-btn-icon">
                            <i
                                className="bi bi-plus-lg"
                                style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 10 }}
                            />
                        </Box>
                        <span>
                            {activeTab == 0
                                ? t('addShop')
                                : activeTab == 1
                                  ? t('addHardwareComponent')
                                  : t('addModule')}
                        </span>
                    </Button>

                    {activeTab == 0 && (
                        <>
                            <Button
                                variant="text"
                                disabled={loading}
                                onClick={handleUpdateCloudConfig}
                                className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                                sx={{ height: 40, placeSelf: 'flex-end' }}
                            >
                                <Box className="pagetitlebar-action-btn-icon">
                                    <Refresh
                                        sx={{
                                            fontSize: '16px',
                                            color: 'inherit',
                                            mr: '6px',
                                            transform: 'rotate(45deg)'
                                        }}
                                    />
                                </Box>
                                <span>{t('updateCloudConfig')}</span>
                            </Button>
                            <Button
                                variant="text"
                                disabled={loading || creating}
                                onClick={handleCopyShop}
                                className="pagetitlebar-action-btn pagetitlebar-action-btn--default"
                                sx={{ height: 40, placeSelf: 'flex-end' }}
                            >
                                <Box className="pagetitlebar-action-btn-icon">
                                    <i
                                        className="bi bi-copy"
                                        style={{ fontSize: '16px', color: 'inherit', lineHeight: 1, paddingRight: 10 }}
                                    />
                                </Box>
                                <span>{t('copyShop')}</span>
                            </Button>
                        </>
                    )}
                </Box>
                <Box sx={{ mt: 3 }}>
                    <TabSelector value={activeTab} onChange={handleTabChange} tabs={tabs}>
                        {activeTab == 0 && (
                            <ShopInformation
                                ref={shopInfoRef}
                                key={`${creating ? 'create' : (shopId ?? 'empty')}-${editorNonce}`}
                                shop={creating ? null : shopForEditor}
                                mode={creating ? ShopEditorMode.Create : ShopEditorMode.Edit}
                                onClose={handleCloseShopInfo}
                                loadingData={loading}
                                onCancel={handleCancelShopInfo}
                                initialReferenceShopId={copyReferenceShopId}
                            />
                        )}

                        {activeTab == 1 && (
                            <HardwareComponents
                                ref={shopInfoRef}
                                shop={shopForEditor}
                                loadingData={loading}
                                creating={isCreating}
                                onCloseCreate={handleCloseComponent}
                                onEditing={() => setIsCreating(false)}
                            />
                        )}

                        {activeTab == 2 && (
                            <SoftwareModules
                                ref={shopInfoRef}
                                shop={shopForEditor}
                                loadingData={loading}
                                creating={isCreating}
                                onCloseCreate={handleCloseModule}
                                onEditing={() => setIsCreating(false)}
                            />
                        )}
                    </TabSelector>
                </Box>
            </Container>
        </Box>
    );
};

ShopSetup.displayName = 'Shop Setup';
export default ShopSetup;
